import React, { useState } from 'react';
import { AppBar, Box, Toolbar, IconButton, Avatar, Menu, MenuItem, useTheme, useMediaQuery, Typography, Badge, Tooltip, Divider } from '@mui/material';
import { Icon } from '@iconify/react';
interface TopBarProps {
  onMenuClick: () => void;
  onLogout: () => void;
}
export const TopBar: React.FC<TopBarProps> = ({
  onMenuClick,
  onLogout
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };
  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };
  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };
  // Placeholder for last login time - in a real app, this would come from your authentication system
  const lastLoginTime = '21-06-2023 14:30:45';
  return <AppBar position="fixed" color="inherit" elevation={0} sx={{
    borderBottom: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
    zIndex: theme => theme.zIndex.drawer + 1
  }} data-id="element-811">
      <Toolbar data-id="element-812">
        {isMobile && <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={onMenuClick} sx={{
        mr: 2,
        color: '#0052CC',
        '&:hover': {
          backgroundColor: '#F0F3F7'
        }
      }} data-id="element-813">
            <Icon icon="lucide:menu" width={24} height={24} data-id="element-814" />
          </IconButton>}
        {isLargeScreen && <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
        height: 32,
        mr: 2
      }} data-id="element-815" />}
        {isMobile && <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
        height: 32,
        mr: 2
      }} data-id="element-816" />}
        <Box sx={{
        flexGrow: 1
      }} data-id="element-817" />
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-818">
          <Typography variant="body2" color="text.secondary" sx={{
          mr: 2,
          display: {
            xs: 'none',
            md: 'block'
          },
          fontSize: '0.75rem'
        }} data-id="element-819">
            Last login: {lastLoginTime}
          </Typography>
          <Tooltip title="Dark mode" arrow data-id="element-820">
            <IconButton size="small" sx={{
            color: '#6B7280',
            backgroundColor: '#F3F5F7',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#E5E7EB'
            },
            mr: 0.5
          }} data-id="element-821">
              <Icon icon="lucide:moon" width={18} height={18} data-id="element-822" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Help" arrow data-id="element-823">
            <IconButton size="small" sx={{
            color: '#6B7280',
            backgroundColor: '#F3F5F7',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#E5E7EB'
            },
            mr: 0.5
          }} data-id="element-824">
              <Icon icon="lucide:help-circle" width={18} height={18} data-id="element-825" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notifications" arrow data-id="element-826">
            <IconButton size="small" onClick={handleNotificationOpen} sx={{
            color: '#6B7280',
            backgroundColor: '#F3F5F7',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#E5E7EB'
            },
            mr: 1
          }} data-id="element-827">
              <Badge badgeContent={3} sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#0052CC',
                color: 'white',
                fontSize: '0.6rem',
                height: 16,
                minWidth: 16
              }
            }} data-id="element-828">
                <Icon icon="lucide:bell" width={18} height={18} data-id="element-829" />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Account settings" arrow data-id="element-830">
            <IconButton edge="end" aria-label="account of current user" aria-haspopup="true" onClick={handleProfileMenuOpen} sx={{
            p: 0.5,
            border: '1px solid #E5E7EB',
            transition: 'none',
            '&:hover': {
              borderColor: '#0052CC'
            }
          }} data-id="element-831">
              <Avatar sx={{
              width: 32,
              height: 32,
              backgroundColor: '#0052CC',
              color: 'white',
              fontWeight: 600
            }} data-id="element-832">
                A
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
      <Menu anchorEl={anchorEl} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} keepMounted transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{
      sx: {
        mt: 1.5,
        borderRadius: 2,
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        minWidth: 180
      }
    }} data-id="element-833">
        <Box sx={{
        px: 2,
        py: 1.5
      }} data-id="element-834">
          <Typography variant="subtitle2" fontWeight={600} data-id="element-835">
            Admin User
          </Typography>
          <Typography variant="caption" color="text.secondary" data-id="element-836">
            admin@openremit.com
          </Typography>
        </Box>
        <Divider data-id="element-837" />
        <MenuItem onClick={handleMenuClose} sx={{
        py: 1.5
      }} data-id="element-838">
          <Icon icon="lucide:user" width={16} height={16} style={{
          marginRight: 8
        }} data-id="element-839" />
          <Typography variant="body2" data-id="element-840">Profile</Typography>
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{
        py: 1.5
      }} data-id="element-841">
          <Icon icon="lucide:settings" width={16} height={16} style={{
          marginRight: 8
        }} data-id="element-842" />
          <Typography variant="body2" data-id="element-843">Settings</Typography>
        </MenuItem>
        <Divider data-id="element-844" />
        <MenuItem onClick={handleLogout} sx={{
        py: 1.5,
        color: 'error.main'
      }} data-id="element-845">
          <Icon icon="lucide:log-out" width={16} height={16} style={{
          marginRight: 8
        }} data-id="element-846" />
          <Typography variant="body2" data-id="element-847">Logout</Typography>
        </MenuItem>
      </Menu>
      <Menu anchorEl={notificationAnchor} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} keepMounted transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} open={Boolean(notificationAnchor)} onClose={handleNotificationClose} PaperProps={{
      sx: {
        mt: 1.5,
        borderRadius: 2,
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        width: 320
      }
    }} data-id="element-848">
        <Box sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-849">
          <Typography variant="subtitle2" fontWeight={600} data-id="element-850">
            Notifications
          </Typography>
          <Typography variant="caption" color="primary" sx={{
          cursor: 'pointer',
          fontWeight: 500
        }} data-id="element-851">
            Mark all as read
          </Typography>
        </Box>
        <Divider data-id="element-852" />
        <Box sx={{
        maxHeight: 320,
        overflow: 'auto'
      }} data-id="element-853">
          {[1, 2, 3].map(item => <Box key={item} sx={{
          p: 2,
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: item === 1 ? '#F0F3F7' : 'transparent',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#F0F3F7'
          }
        }} data-id="element-854">
              <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 1
          }} data-id="element-855">
                <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: item === 1 ? '#0052CC' : 'transparent',
              mr: 1,
              mt: 0.5
            }} data-id="element-856" />
                <Typography variant="body2" fontWeight={item === 1 ? 600 : 400} data-id="element-857">
                  {item === 1 ? 'New partner registration request' : item === 2 ? 'System maintenance scheduled' : 'Compliance alert: Verification needed'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" data-id="element-858">
                {item === 1 ? '5 minutes ago' : item === 2 ? '2 hours ago' : '1 day ago'}
              </Typography>
            </Box>)}
        </Box>
        <Box sx={{
        p: 1.5,
        textAlign: 'center',
        borderTop: '1px solid #E5E7EB'
      }} data-id="element-859">
          <Typography variant="body2" color="primary" sx={{
          cursor: 'pointer',
          fontWeight: 500,
          '&:hover': {
            textDecoration: 'underline'
          }
        }} data-id="element-860">
            View all notifications
          </Typography>
        </Box>
      </Menu>
    </AppBar>;
};
export default TopBar;